import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import Paginate from '@/components/pagination/Paginate';
import { ORDER_STATUS_CMS, ORDER_STATUS_LIST } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ORDER_STATUS } from '@/types/enums';
import { monthsCombobox, yearsListCombobox } from '@/utils/datetime';
import { scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import RentalHistoryItem from './component/RentalHistoryItem';
import { getYear } from 'date-fns';

export default observer(function RentalHistoryList() {
    // hook
    const { t } = useTranslation();

    // store
    const {
        reservationStore: { reservationENDList, paging, totalPage, keepSearchParams, defaultPaging, getReservationENDList, setObservable },
        regionProvinceStore: { getLocationsByRegionProvince, locationSelects }
    } = useStore();

    // life cycle
    useEffect(() => {
        getReservationENDList(keepSearchParams || {}, paging);
        getLocationsByRegionProvince({});
        return () => {
            // clean();
        };
    }, []);

    // const
    const currentYear = getYear(new Date());
    const years = yearsListCombobox();
    const months = monthsCombobox;

    // validate schema
    const validateSchema = yup.object().shape({
        orderStatus: yup.number(),
        year: yup.string(),
        month: yup.string(),
        locationId: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            orderStatus: keepSearchParams?.orderStatus?.toString() ?? '',
            locationId: keepSearchParams?.locationName?.toString() ?? '',
            year: keepSearchParams?.year ?? currentYear.toString(),
            month: keepSearchParams?.month ?? ''
        }
    });

    const OrderSatusList = ORDER_STATUS_LIST.filter(status => status !== ORDER_STATUS.Operation).map(status => ({
        label: t(ORDER_STATUS_CMS[status].key),
        value: status.toString(),
        code: status.toString()
    }));

    const formWatch = form.watch();
    useEffect(() => {
        const searchParams = { ...formWatch };
        getReservationENDList(searchParams, defaultPaging);
        setObservable('keepSearchParams', searchParams, { isMergeObject: false });
    }, [JSON.stringify(formWatch)]);

    return (
        <div className='layout-card space-y-6'>
            <Form {...form}>
                <form>
                    <div className='grid md:grid-cols-4 xsm:grid-cols-2 grid-cols-1 md:gap-4 gap-2'>
                        <FormField
                            control={form.control}
                            name='orderStatus'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.reservation.reservation_status')}
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value);
                                            }}
                                            search={false}
                                            items={
                                                OrderSatusList
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='year'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.year')}
                                            value={field.value ? field.value.toString() : ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                            }}
                                            disableClearIcon
                                            search={false}
                                            items={years.map(year => ({
                                                label: `${year}年`,
                                                value: year.toString()
                                            }))}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='month'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.month')}
                                            value={field.value ? field.value.toString() : ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                            }}
                                            search={false}
                                            items={months.map(month => ({
                                                label: `${month}月`,
                                                value: month.toString()
                                            }))}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='locationId'
                            render={({ field }) => (
                                <FormItem className=''>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.location_name')}
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                            }}
                                            search={false}
                                            items={
                                                (Array.isArray(locationSelects) && locationSelects?.length ?
                                                    [
                                                        ...locationSelects.map(item => ({
                                                            label: item.name ?? '',
                                                            value: item.id.toString(),
                                                            code: item.id
                                                        }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    </div>
                </form>
            </Form>

            {reservationENDList && reservationENDList.length > 0 ?
                (
                    reservationENDList.map(reservation => (
                        <RentalHistoryItem key={reservation.id} reservation={reservation} />
                    ))
                ) :
                (
                    <div className='text-center'>{t('sentences.no_data')}</div>
                )}

            <div className='flex justify-center'>
                {totalPage > 0 && (
                    <Paginate
                        forcePage={paging.pagination.pageIndex}
                        pageCount={totalPage}
                        onPageChange={({ selected }) => {
                            scrollToTop();
                            paging.pagination.pageIndex = selected;
                            getReservationENDList(keepSearchParams, paging);
                        }}
                    />
                )}
            </div>
        </div>
    );
});
